import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import accountingService, { CashboxDto } from '../../../services/accountingService';
import { CourierBranchSetting, courierModuleService } from './courierModuleService';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Row {
  branchId: string;
  branchName: string;
  enabled: boolean;
  settlementCashboxId: number | '';
  updatedBy?: string | null;
  dirty: boolean;
}

/** Şube bazlı kurye ayarı: kurye kasası (nakit gün sonuna kadar kuryenin kasasında durur). */
const CourierSettingsDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [cashboxes, setCashboxes] = useState<CashboxDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaved(false);
    setLoading(true);
    Promise.all([
      courierModuleService.settings(),
      accountingService.getCashboxes().catch(() => [] as CashboxDto[]),
    ])
      .then(([settings, boxes]) => {
        const byBranch = new Map<string, CourierBranchSetting>(settings.map((s) => [s.branchId, s]));
        setRows(branches.map((b: any) => {
          const s = byBranch.get(b.id);
          return {
            branchId: b.id,
            branchName: b.name,
            enabled: !!s?.courierCashboxEnabled,
            settlementCashboxId: s?.settlementCashboxId ?? '',
            updatedBy: s?.updatedBy,
            dirty: false,
          };
        }));
        // Kurye kasaları şube kasası olarak seçilmez
        setCashboxes(boxes.filter((c) => !c.IsDeleted && !(c.Name || '').startsWith('Kurye · ')));
      })
      .catch((e: any) => setError(e?.message || t('courierModule.settings.loadFailed')))
      .finally(() => setLoading(false));
  }, [open, branches, t]);

  const update = (branchId: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.branchId === branchId ? { ...r, ...patch, dirty: true } : r)));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const r of rows.filter((x) => x.dirty)) {
        await courierModuleService.saveSetting({
          branchId: r.branchId,
          courierCashboxEnabled: r.enabled,
          settlementCashboxId: r.settlementCashboxId === '' ? null : Number(r.settlementCashboxId),
        });
      }
      setRows((prev) => prev.map((r) => ({ ...r, dirty: false })));
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || t('courierModule.settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle fontWeight={800}>{t('courierModule.settings.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Alert severity="info">{t('courierModule.settings.info')}</Alert>
          {error && <Alert severity="error">{error}</Alert>}
          {saved && <Alert severity="success">{t('courierModule.settings.saved')}</Alert>}
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
          ) : rows.map((r) => (
            <Paper key={r.branchId} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>{r.branchName}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Switch checked={r.enabled} onChange={(e) => update(r.branchId, { enabled: e.target.checked })} />
                  <Typography variant="body2">{t('courierModule.settings.courierCashbox')}</Typography>
                </Stack>
                <TextField
                  select
                  size="small"
                  label={t('courierModule.settings.settlementCashbox')}
                  value={r.settlementCashboxId}
                  onChange={(e) => update(r.branchId, { settlementCashboxId: e.target.value === '' ? '' : Number(e.target.value) })}
                  disabled={!r.enabled}
                  sx={{ minWidth: 240 }}
                >
                  <MenuItem value="">{t('courierModule.settings.screenCashbox')}</MenuItem>
                  {cashboxes.map((c) => (
                    <MenuItem key={c.Id} value={c.Id}>{c.Name}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              {r.updatedBy && (
                <Typography variant="caption" color="text.secondary">
                  {t('courierModule.settings.updatedBy', { name: r.updatedBy })}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('courierModule.settings.close')}</Button>
        <Button variant="contained" onClick={save} disabled={saving || !rows.some((r) => r.dirty)}>
          {saving ? <CircularProgress size={18} color="inherit" /> : t('courierModule.settings.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourierSettingsDialog;
